import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, DollarSign, Users, Building2, Mail, Phone, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCareerPageData, formatCareerPageData, getJobOffers, formatJobOffers } from '../api/strapi/careersPageApi';
import SectionGuard from '../components/SectionGuard';
import DataLoader from '../components/loader/DataLoader';
import PageHero from '../components/common/PageHero';

const CareerPage = () => {
  const intl = useIntl();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filters, setFilters] = useState({
    location: 'all',
    jobType: 'all',
    department: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchCareerData = async () => {
      try {
        setLoading(true);
        
        // Récupérer les données de la page carrière
        const result = await getCareerPageData();
        
        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Career page data unavailable:", result);
          }
          
          // Charger les offres d'emploi à partir d'une autre source
          const jobsResult = await getJobOffers();
          
          if (jobsResult.ok && Array.isArray(jobsResult.data)) {
            const formattedJobs = formatJobOffers(jobsResult.data);
            setJobs(formattedJobs);
          } else {
            // Si aucune donnée n'est disponible, utiliser des données par défaut
            const defaultJobs = [
              {
                id: 1,
                title: intl.formatMessage({ id: "career.opportunity.fullstack.developer.title" }),
                department: intl.formatMessage({ id: "career.opportunity.fullstack.developer.department" }),
                location: intl.formatMessage({ id: "career.opportunity.fullstack.developer.location" }),
                type: intl.formatMessage({ id: "full.time" }),
                salary: "Compétitif",
                description: intl.formatMessage({ id: "career.opportunity.fullstack.developer.description" }),
                requirements: [
                  "Licence en informatique ou domaine connexe",
                  "3+ ans d'expérience en développement full-stack",
                  "Maîtrise de JavaScript, React, Node.js",
                  "Expérience avec les bases de données (SQL/NoSQL)"
                ],
                postedDate: "2024-01-15",
                closingDate: "2024-02-15"
              },
              {
                id: 2,
                title: intl.formatMessage({ id: "career.opportunity.iot.engineer.title" }),
                department: intl.formatMessage({ id: "career.opportunity.iot.engineer.department" }),
                location: intl.formatMessage({ id: "career.opportunity.iot.engineer.location" }),
                type: intl.formatMessage({ id: "full.time" }),
                salary: "Compétitif",
                description: intl.formatMessage({ id: "career.opportunity.iot.engineer.description" }),
                requirements: [
                  "Licence en génie électrique ou domaine connexe",
                  "2+ ans d'expérience avec les technologies IoT",
                  "Connaissance des systèmes embarqués et des capteurs",
                  "Expérience avec les plateformes cloud (AWS, Azure)"
                ],
                postedDate: "2024-01-10",
                closingDate: "2024-02-10"
              },
              {
                id: 3,
                title: intl.formatMessage({ id: "career.opportunity.ai.specialist.title" }),
                department: intl.formatMessage({ id: "career.opportunity.ai.specialist.department" }),
                location: intl.formatMessage({ id: "career.opportunity.ai.specialist.location" }),
                type: intl.formatMessage({ id: "full.time" }),
                salary: "Compétitif",
                description: intl.formatMessage({ id: "career.opportunity.ai.specialist.description" }),
                requirements: [
                  "Master en IA, Apprentissage automatique ou domaine connexe",
                  "Expérience avec TensorFlow, PyTorch",
                  "Solide base mathématique",
                  "Expérience en TLP ou vision par ordinateur"
                ],
                postedDate: "2024-01-05",
                closingDate: "2024-02-05"
              },
              {
                id: 4,
                title: intl.formatMessage({ id: "career.opportunity.cybersecurity.consultant.title" }),
                department: intl.formatMessage({ id: "career.opportunity.cybersecurity.consultant.department" }),
                location: intl.formatMessage({ id: "career.opportunity.cybersecurity.consultant.location" }),
                type: intl.formatMessage({ id: "full.time" }),
                salary: "Compétitif",
                description: intl.formatMessage({ id: "career.opportunity.cybersecurity.consultant.description" }),
                requirements: [
                  "Licence en cybersécurité ou domaine connexe",
                  "Expérience en évaluations et audits de sécurité",
                  "Connaissance des cadres de sécurité",
                  "Certifications comme CISSP, CEH sont un plus"
                ],
                postedDate: "2024-01-20",
                closingDate: "2024-02-20"
              }
            ];
            setJobs(defaultJobs);
          }
        } else {
          // Si les données de la page carrière sont disponibles
          const formattedData = formatCareerPageData(result.data);
          setJobs(formattedData.jobs || []);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des offres d'emploi:", err);
        setError(intl.formatMessage({ id: "common.contentUnavailable" }));
      } finally {
        setLoading(false);
      }
    };

    fetchCareerData();
  }, [intl]);

  // Filtrer les emplois en fonction des critères
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          job.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = filters.location === 'all' || job.location.includes(filters.location);
    const matchesJobType = filters.jobType === 'all' || job.type === filters.jobType;
    const matchesDepartment = filters.department === 'all' || job.department.includes(filters.department);
    
    return matchesSearch && matchesLocation && matchesJobType && matchesDepartment;
  });

  if (loading) {
    return <DataLoader isLoading={true} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0a1628] mb-2">{intl.formatMessage({ id: "common.contentUnavailableTitle" })}</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{intl.formatMessage({ id: "careers.title" })} | Africa Systems</title>
        <meta name="description" content={intl.formatMessage({ id: "careers.description" })} />
      </Helmet>

      {/* Hero Section */}
      <PageHero
        eyebrow="Africa Systems"
        title={intl.formatMessage({ id: "careers.title" })}
        description={intl.formatMessage({ id: "careers.description" })}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <button className="bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors">
            {intl.formatMessage({ id: "spontaneous.application.button" })}
          </button>
        </motion.div>
      </PageHero>

      {/* Stats Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#16A34A]/10 rounded-full mb-4">
                <Users className="w-8 h-8 text-[#16A34A]" />
              </div>
              <div className="text-3xl font-bold text-[#0a1628] mb-2">85+</div>
              <div className="text-slate-600">{intl.formatMessage({ id: "employees.across.africa" })}</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#16A34A]/10 rounded-full mb-4">
                <Building2 className="w-8 h-8 text-[#16A34A]" />
              </div>
              <div className="text-3xl font-bold text-[#0a1628] mb-2">5</div>
              <div className="text-slate-600">{intl.formatMessage({ id: "countries.with.opportunities" })}</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#16A34A]/10 rounded-full mb-4">
                <Briefcase className="w-8 h-8 text-[#16A34A]" />
              </div>
              <div className="text-3xl font-bold text-[#0a1628] mb-2">12+</div>
              <div className="text-slate-600">{intl.formatMessage({ id: "open.positions" })}</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#16A34A]/10 rounded-full mb-4">
                <Calendar className="w-8 h-8 text-[#16A34A]" />
              </div>
              <div className="text-3xl font-bold text-[#0a1628] mb-2">5</div>
              <div className="text-slate-600">{intl.formatMessage({ id: "continents.of.impact" })}</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a1628]">
              {intl.formatMessage({ id: "available.positions" })}
            </h2>
            
            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder={intl.formatMessage({ id: "search.jobs" })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
              />
              <Briefcase className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {intl.formatMessage({ id: "filter.by.location" })}
              </label>
              <select
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
              >
                <option value="all">{intl.formatMessage({ id: "all.locations" })}</option>
                <option value="Cameroon">{intl.formatMessage({ id: "cameroon" })}</option>
                <option value="Kenya">{intl.formatMessage({ id: "kenya" })}</option>
                <option value="Nigeria">{intl.formatMessage({ id: "nigeria" })}</option>
                <option value="South Africa">{intl.formatMessage({ id: "south.africa" })}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {intl.formatMessage({ id: "filter.by.type" })}
              </label>
              <select
                value={filters.jobType}
                onChange={(e) => setFilters({...filters, jobType: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
              >
                <option value="all">{intl.formatMessage({ id: "all.types" })}</option>
                <option value="Full-time">{intl.formatMessage({ id: "full.time" })}</option>
                <option value="Part-time">{intl.formatMessage({ id: "part.time" })}</option>
                <option value="Contract">{intl.formatMessage({ id: "contract" })}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {intl.formatMessage({ id: "filter.by.department" })}
              </label>
              <select
                value={filters.department}
                onChange={(e) => setFilters({...filters, department: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
              >
                <option value="all">{intl.formatMessage({ id: "all.departments" })}</option>
                <option value="Development">{intl.formatMessage({ id: "development" })}</option>
                <option value="Security">{intl.formatMessage({ id: "cybersecurity" })}</option>
                <option value="AI">{intl.formatMessage({ id: "ai.ml" })}</option>
                <option value="IoT">{intl.formatMessage({ id: "iot" })}</option>
              </select>
            </div>
          </div>

          {/* Job Listings */}
          <div className="space-y-6">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#0a1628] mb-2">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                        <div className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-1" />
                          {job.department}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {job.type}
                        </div>
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 mr-1" />
                          {job.salary}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <button 
                        onClick={() => setSelectedJob(job)}
                        className="bg-[#16A34A] hover:bg-[#15803D] text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                      >
                        {intl.formatMessage({ id: "view.details" })}
                      </button>
                      <button className="border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A]/10 px-4 py-2 rounded-lg font-medium transition-colors duration-200">
                        {intl.formatMessage({ id: "apply.now" })}
                      </button>
                    </div>
                  </div>
                  
                  <p className="mt-4 text-slate-700">{job.description}</p>
                  
                  <div className="mt-4 flex items-center text-sm text-slate-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{intl.formatMessage({ id: "posted.on" })}: {new Date(job.postedDate).toLocaleDateString()}</span>
                    <span className="mx-2">•</span>
                    <span>{intl.formatMessage({ id: "closing.on" })}: {new Date(job.closingDate).toLocaleDateString()}</span>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12">
                <Briefcase className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#0a1628] mb-2">
                  {intl.formatMessage({ id: "no.jobs.found" })}
                </h3>
                <p className="text-slate-600">
                  {intl.formatMessage({ id: "no.jobs.match.criteria" })}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Job Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-[#0a1628]">{selectedJob.title}</h3>
                <button 
                  onClick={() => setSelectedJob(null)}
                  className="text-slate-500 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-6">
                <div className="flex items-center">
                  <Briefcase className="w-4 h-4 mr-1" />
                  {selectedJob.department}
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {selectedJob.location}
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {selectedJob.type}
                </div>
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 mr-1" />
                  {selectedJob.salary}
                </div>
              </div>
              
              <div className="prose max-w-none mb-6">
                <h4 className="text-lg font-semibold text-[#0a1628] mb-2">
                  {intl.formatMessage({ id: "job.description" })}
                </h4>
                <p>{selectedJob.description}</p>
                
                <h4 className="text-lg font-semibold text-[#0a1628] mt-6 mb-2">
                  {intl.formatMessage({ id: "requirements" })}
                </h4>
                <ul className="list-disc pl-5 space-y-1">
                  {selectedJob.requirements.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button className="bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200">
                  {intl.formatMessage({ id: "apply.for.position" })}
                </button>
                <button 
                  onClick={() => setSelectedJob(null)}
                  className="border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                >
                  {intl.formatMessage({ id: "close" })}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Why Join Us Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a1628] mb-4">
              {intl.formatMessage({ id: "why.join.us" })}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {intl.formatMessage({ id: "why.join.us.description" })}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: intl.formatMessage({ id: "innovative.environment" }),
                description: intl.formatMessage({ id: "innovative.environment.desc" }),
                icon: <Building2 className="w-8 h-8 text-[#16A34A]" />
              },
              {
                title: intl.formatMessage({ id: "learning.growth" }),
                description: intl.formatMessage({ id: "learning.growth.desc" }),
                icon: <Users className="w-8 h-8 text-[#16A34A]" />
              },
              {
                title: intl.formatMessage({ id: "competitive.benefits" }),
                description: intl.formatMessage({ id: "competitive.benefits.desc" }),
                icon: <DollarSign className="w-8 h-8 text-[#16A34A]" />
              },
              {
                title: intl.formatMessage({ id: "work.life.balance" }),
                description: intl.formatMessage({ id: "work.life.balance.desc" }),
                icon: <Clock className="w-8 h-8 text-[#16A34A]" />
              },
              {
                title: intl.formatMessage({ id: "impactful.work" }),
                description: intl.formatMessage({ id: "impactful.work.desc" }),
                icon: <Briefcase className="w-8 h-8 text-[#16A34A]" />
              },
              {
                title: intl.formatMessage({ id: "collaborative.team" }),
                description: intl.formatMessage({ id: "collaborative.team.desc" }),
                icon: <Users className="w-8 h-8 text-[#16A34A]" />
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0a1628] mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-slate-600">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto bg-[#0a1628] rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {intl.formatMessage({ id: "still.have.questions" })}
            </h2>
            <p className="text-xl mb-8 text-white/75">
              {intl.formatMessage({ id: "contact.our.hr.team" })}
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <div className="flex items-center justify-center gap-3">
                <Mail className="w-6 h-6" />
                <span>careers@africasystems.com</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Phone className="w-6 h-6" />
                <span>+237 123 456 789</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareerPage;